import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { formatDateTime } from '../utils/dateUtils';
import LoadingSpinner from '../components/LoadingSpinner';
import api from '../utils/api';
import RequestStatusBadge from '../components/RequestStatusBadge';
import RequestActionModal from '../components/RequestActionModal';

function DonorRequestsPage() {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'pending', 'approved', 'rejected', 'fulfilled'
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState(null); // 'approve', 'reject', 'fulfill'
  
  // Track requests loading state
  const [loadingState, setLoadingState] = useState({
    initial: true,
    refreshing: false
  });

  // Define fetchRequests before using it in useEffect
  const fetchRequests = useCallback(async (isRefresh = false) => {
    if (!user || user.role !== 'donor') {
      setError('Unauthorized. Only donors can access this page.');
      setLoading(false);
      setLoadingState({ initial: false, refreshing: false });
      return;
    }
    
    if (!isRefresh) {
      setLoading(true);
    } else {
      setLoadingState(prev => ({ ...prev, refreshing: true }));
    }
    
    try {
      // Use the api utility which has the correct baseURL configuration
      const response = await api.get('/requests/my-listings');
      console.log('API response:', response.data);
      
      // Handle nested structure where requests.results contains the actual array
      if (response.data.requests && response.data.requests.results) {
        // Extract the requests array from the nested structure
        setRequests(response.data.requests.results);
      } else if (Array.isArray(response.data.requests)) {
        // Handle case where requests is directly an array
        setRequests(response.data.requests);
      } else {
        console.warn('API did not return requests in expected format:', response.data);
        setRequests([]);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch requests. Please try again later.');
    } finally {
      setLoading(false);
      setLoadingState({ initial: false, refreshing: false });
    }
  }, [user]); // Remove showNotification from dependencies as it's not used in the function
  
  // Use fetchRequests in useEffect
  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // fetchRequests is now defined above

  // Filter requests based on active tab
  const filteredRequests = requests.filter(request => {
    if (activeTab === 'all') return true;
    return request.status === activeTab;
  });

  // Open action modal
  const openActionModal = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setActionModalOpen(true);
  };

  // Handle request approval
  const handleApprove = async (requestId, data) => {
    try {
      setLoading(true);
      const response = await api.put(`/requests/${requestId}/approve`, {
        pickup_time: data.pickupTime,
        notes: data.notes
      });
      
      // Get the updated request from the response
      const updatedRequest = response.data.request;
      
      // Update the local state with the complete updated request
      setRequests(requests.map(request => 
        request.id === requestId ? updatedRequest : request
      ));
      
      showNotification('Request approved successfully!', 'success');
      closeActionModal();
    } catch (err) {
      console.error('Error approving request:', err);
      showNotification(err.response?.data?.message || err.message || 'Failed to approve request', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle request rejection
  const handleReject = async (requestId, data) => {
    try {
      setLoading(true);
      const response = await api.put(`/requests/${requestId}/reject`, { 
        reason: data.reason 
      });
      
      // Get the updated request from the response
      const updatedRequest = response.data.request;
      
      // Update the local state with the complete updated request
      setRequests(requests.map(request => 
        request.id === requestId ? updatedRequest : request
      ));
      
      showNotification('Request rejected successfully', 'success');
      closeActionModal();
    } catch (err) {
      console.error('Error rejecting request:', err);
      showNotification(err.response?.data?.message || err.message || 'Failed to reject request', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle request fulfillment
  const handleFulfill = async (requestId) => {
    try {
      setLoading(true);
      const response = await api.put(`/requests/${requestId}/fulfill`);
      
      // Get the updated request from the response
      const updatedRequest = response.data.request;
      
      // Update the local state with the complete updated request
      setRequests(requests.map(request => 
        request.id === requestId ? updatedRequest : request
      ));
      
      showNotification('Request marked as fulfilled!', 'success');
      closeActionModal();
    } catch (err) {
      console.error('Error fulfilling request:', err);
      showNotification(err.response?.data?.message || err.message || 'Failed to fulfill request', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Close action modal and reset selection
  const closeActionModal = () => {
    setActionModalOpen(false);
    setSelectedRequest(null);
    setActionType(null);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Food Donation Requests
          </h1>
          <p className="text-gray-600 mt-1">
            Manage requests for your food listings
          </p>
        </div>
        <div className="flex space-x-3 mt-4 sm:mt-0">
          <button
            className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            onClick={() => fetchRequests(true)}
            disabled={loadingState.refreshing}
          >
            {loadingState.refreshing ? (
              <>
                <span className="mr-2">Refreshing...</span>
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </>
            ) : (
              <>
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </>
            )}
          </button>
          <Link
            to="/listings/my"
            className="rounded-md bg-green-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
          >
            Manage My Listings
          </Link>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('all')}
            className={`${
              activeTab === 'all'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            All
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`${
              activeTab === 'pending'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Pending
          </button>
          <button
            onClick={() => setActiveTab('approved')}
            className={`${
              activeTab === 'approved'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Approved
          </button>
          <button
            onClick={() => setActiveTab('rejected')}
            className={`${
              activeTab === 'rejected'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Rejected
          </button>
          <button
            onClick={() => setActiveTab('fulfilled')}
            className={`${
              activeTab === 'fulfilled'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Fulfilled
          </button>
        </nav>
      </div>
      
      {/* Loading state */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No requests found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {activeTab !== 'all' 
              ? `You don't have any ${activeTab} requests at the moment.`
              : "You haven't received any requests for your food listings yet."
            }
          </p>
        </div>
      ) : (
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                  Requested By
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Food Item
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Quantity
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Status
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Date
                </th>
                <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredRequests.map((request) => (
                <tr key={request.id}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                    <div className="font-medium text-gray-900">
                      {request.recipient_name || request.organization_name || "Anonymous"}
                    </div>
                    <div className="text-gray-500">
                      {request.recipient_id}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <div className="font-medium text-gray-900">
                      {request.listing_title}
                    </div>
                    <div className="text-gray-500">
                      ID: {request.listing_id}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {`${request.quantity} ${request.quantity_unit || ""}`}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <RequestStatusBadge status={request.status} />
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {formatDateTime(request.created_at)}
                  </td>
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                    {request.status === 'pending' ? (
                      <div className="flex space-x-2 justify-end">
                        <button
                          onClick={() => openActionModal(request, 'approve')}
                          className="text-green-600 hover:text-green-900"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => openActionModal(request, 'reject')}
                          className="text-red-600 hover:text-red-900"
                        >
                          Reject
                        </button>
                      </div>
                    ) : request.status === 'approved' ? (
                      <button
                        onClick={() => openActionModal(request, 'fulfill')}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Mark Fulfilled
                      </button>
                    ) : (
                      <Link
                        to={`/requests/${request.id}`}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        View Details
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Action Modals */}
      {actionModalOpen && selectedRequest && (
        <RequestActionModal
          isOpen={actionModalOpen}
          onClose={closeActionModal}
          requestId={selectedRequest.id}
          requestTitle={selectedRequest.listing_title}
          actionType={actionType}
          onApprove={handleApprove}
          onReject={handleReject}
          onFulfill={handleFulfill}
        />
      )}
    </div>
  );
}

export default DonorRequestsPage;
